import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const FinancingStep = ({
  formData,
  onChange,
  errors = {},
  calculatedValues = {}
}) => {
  const paymentFrequencies = [
    { value: 'monthly', label: 'Mensuel' },
    { value: 'biweekly', label: 'Aux 2 semaines' },
    { value: 'weekly', label: 'Hebdomadaire' },
    { value: 'accelerated_biweekly', label: 'Aux 2 semaines accéléré' }
  ];

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(value);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8 mb-4 md:mb-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="DollarSign" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
              Achat et financement
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              Prix, mise de fonds et hypothèque
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prix d'achat"
              tooltip="Le montant total payé pour l'acquisition de l'immeuble, avant les frais de clôture."
              type="number"
              placeholder="Ex: 450000"
              value={formData?.purchasePrice || ''}
              onChange={(e) => onChange('purchasePrice', e?.target?.value)}
              error={errors?.purchasePrice}
              required
              min="0"
            />

            <Input
              label="Mise de fonds (%)"
              tooltip="Le pourcentage du prix d'achat que vous payez comptant (habituellement 5%, 10% ou 20%)."
              type="number"
              placeholder="Ex: 20"
              value={formData?.downPaymentPercent || ''}
              onChange={(e) => onChange('downPaymentPercent', e?.target?.value)}
              error={errors?.downPaymentPercent}
              required
              min="0"
              max="100"
            />
          </div>

          {calculatedValues?.downPaymentAmount && (
            <div className="bg-muted rounded-lg p-3 md:p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm md:text-base text-muted-foreground">
                  Montant de la mise de fonds
                </span>
                <span className="text-base md:text-lg font-semibold text-foreground data-text">
                  {formatCurrency(calculatedValues?.downPaymentAmount)}
                </span>
              </div>
              {calculatedValues?.schlPremiumAmount > 0 && (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm md:text-base text-muted-foreground">
                    Prime SCHL
                  </span>
                  <span className="text-base md:text-lg font-semibold text-foreground data-text">
                    {formatCurrency(calculatedValues?.schlPremiumAmount)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm md:text-base text-muted-foreground">
                  Montant hypothécaire
                </span>
                <span className="text-base md:text-lg font-semibold text-foreground data-text">
                  {formatCurrency(calculatedValues?.mortgageAmount)}
                </span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Taux d'intérêt (%)"
              tooltip="Le taux d'intérêt annuel de votre prêt hypothécaire. Influence directement votre mensualité."
              type="number"
              placeholder="Ex: 5.5"
              value={formData?.interestRate || ''}
              onChange={(e) => onChange('interestRate', e?.target?.value)}
              error={errors?.interestRate}
              required
              min="0"
              max="20"
              step="0.1"
            />

            <Input
              label="Période d'amortissement (ans)"
              tooltip="Le nombre d'années sur lequel le remboursement de l'hypothèque est calculé (souvent 25 ou 30 ans)."
              type="number"
              placeholder="Ex: 25"
              value={formData?.amortizationPeriod || ''}
              onChange={(e) => onChange('amortizationPeriod', e?.target?.value)}
              error={errors?.amortizationPeriod}
              required
              min="0"
              max="50"
              step="1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Fréquence de paiement"
              options={paymentFrequencies}
              value={formData?.paymentFrequency || ''}
              onChange={(value) => onChange('paymentFrequency', value)}
              error={errors?.paymentFrequency}
              required
              placeholder="Sélectionner la fréquence"
            />

            <Input
              label="Prime SCHL (%)"
              tooltip="Assurance prêt hypothécaire obligatoire si la mise de fonds est inférieure à 20%."
              type="number"
              placeholder="Optionnel (ex: 4)"
              value={formData?.schlPremium || ''}
              onChange={(e) => onChange('schlPremium', e?.target?.value)}
              error={errors?.schlPremium}
              min="0"
              max="100"
              step="0.1"
              description="% du montant du prêt (après mise de fonds)"
            />
          </div>

          {calculatedValues?.monthlyPayment && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Calculator" size={20} color="var(--color-primary)" />
                <span className="text-sm md:text-base font-medium text-foreground">
                  Paiement hypothécaire estimé
                </span>
              </div>
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary data-text">
                {formatCurrency(calculatedValues?.monthlyPayment)}/mois
              </div>
            </div>
          )}

          <div className="border-t border-border pt-4 md:pt-6">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              Rénovations et améliorations
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Coût des rénovations"
                type="number"
                placeholder="Ex: 50000"
                value={formData?.renovationCost || ''}
                onChange={(e) => onChange('renovationCost', e?.target?.value)}
                error={errors?.renovationCost}
                min="0"
              />

              <Input
                label="Augmentation de valeur estimée (%)"
                tooltip="L'augmentation prévue de la valeur marchande de l'immeuble suite aux travaux de rénovation effectués."
                type="number"
                placeholder="Ex: 15"
                value={formData?.valueIncreasePercent || ''}
                onChange={(e) => onChange('valueIncreasePercent', e?.target?.value)}
                error={errors?.valueIncreasePercent}
                min="0"
                max="100"
              />
            </div>

            {calculatedValues?.afterRenovationValue && (
              <div className="bg-success/5 border border-success/20 rounded-lg p-3 md:p-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm md:text-base text-muted-foreground">
                    Valeur après rénovations
                  </span>
                  <span className="text-base md:text-lg font-semibold text-success data-text">
                    {formatCurrency(calculatedValues?.afterRenovationValue)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Frais de clôture estimés"
              tooltip="Inclut la taxe de bienvenue, les frais de notaire et les ajustements de taxes."
              type="number"
              placeholder="Ex: 5000"
              value={formData?.closingCosts || ''}
              onChange={(e) => onChange('closingCosts', e?.target?.value)}
              error={errors?.closingCosts}
              min="0"
            />

            <Input
              label="Autres frais initiaux"
              tooltip="Inclut les frais d'inspection, d'évaluation, les tests environnementaux ou autres coûts de départ."
              type="number"
              placeholder="Ex: 2000"
              value={formData?.otherInitialCosts || ''}
              onChange={(e) => onChange('otherInitialCosts', e?.target?.value)}
              error={errors?.otherInitialCosts}
              min="0"
            />
          </div>

          {calculatedValues?.totalInitialInvestment && (
            <div className="bg-muted rounded-lg p-4 md:p-6">
              <div className="flex items-center justify-between">
                <span className="text-base md:text-lg font-medium text-foreground">
                  Investissement initial total
                </span>
                <span className="text-xl md:text-2xl font-bold text-foreground data-text">
                  {formatCurrency(calculatedValues?.totalInitialInvestment)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancingStep;